import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Button, Input, MessageBarActions, MessageBarBody, MessageBarTitle, SearchBox, Spinner, Textarea } from '@fluentui/react-components';
import { Add16Regular, Delete16Regular, Edit16Regular, Tag20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../../redux/reducers';
import { ErrorType } from '../../../models/HttpError';
import { MedicalServiceCategory } from '../../../models/Entities/MedicalServices/MedicalServiceHierarchy';
import * as CategoriesService from '../../../services/MedicalServiceCategories.Services';

// guía
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// lógica
import { CategoryDraft, DESCRIPTION_MAX, DraftErrors, NAME_MAX, draftOf, filterCategories, hasErrors, isInUseError, sortByName, toPayload, validateDraft } from './MedicalServiceCategories.Logic';

import './MedicalServiceCategories.Styles.css';

export interface MedicalServiceCategoriesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Se avisa tras cada alta, edición o baja exitosa, para que el selector se refresque. */
  onChanged?: () => void;
}

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; category: MedicalServiceCategory };

/**
 * Alta, edición y baja de las categorías de servicios médicos (guía, drawer large). El
 * formulario va embebido arriba de la lista. Sin store: las categorías se piden al abrir y
 * tras cada cambio, directo al servicio.
 */
export const MedicalServiceCategoriesDrawerComponent = ({ isOpen, onClose, onChanged }: MedicalServiceCategoriesDrawerProps) => {
  const [t] = useTranslation();
  const token = useSelector((state: AppState) => state.oidc?.user?.access_token);

  const [categories, setCategories] = useState<MedicalServiceCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<ErrorType | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ErrorType | undefined>(undefined);
  const [deletingId, setDeletingId] = useState<number | undefined>(undefined);
  const [deleteError, setDeleteError] = useState<ErrorType | undefined>(undefined);

  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [draft, setDraft] = useState<CategoryDraft>(draftOf());
  const [touched, setTouched] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | undefined>(undefined);
  const nameRef = useRef<HTMLInputElement | null>(null);

  const load = () => {
    if (!token) return;
    setLoading(true);
    setLoadError(undefined);
    Promise.resolve()
      .then(() => CategoriesService.GetAllMedicalServiceCategories(token))
      .then((list) => { setCategories(sortByName(list ?? [])); setLoaded(true); })
      .catch((error: ErrorType) => setLoadError(error))
      .finally(() => setLoading(false));
  };

  // Al abrir: lista fresca y sin restos de la vez anterior.
  useEffect(() => {
    if (!isOpen) return;
    setMode({ kind: 'idle' });
    setDraft(draftOf());
    setTouched(false);
    setSearch('');
    setConfirmDeleteId(undefined);
    setSaveError(undefined);
    setDeleteError(undefined);
    load();
  }, [isOpen, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode.kind !== 'idle') nameRef.current?.focus();
  }, [mode]);

  const editingId = mode.kind === 'edit' ? mode.category.id : undefined;
  const errors: DraftErrors = useMemo(() => validateDraft(draft, categories, editingId), [draft, categories, editingId]);
  const visible = useMemo(() => filterCategories(categories, search), [categories, search]);

  const startNew = () => { setSaveError(undefined); setDraft(draftOf()); setTouched(false); setMode({ kind: 'new' }); };
  const startEdit = (category: MedicalServiceCategory) => { setSaveError(undefined); setDraft(draftOf(category)); setTouched(false); setMode({ kind: 'edit', category }); };
  const cancelForm = () => { setMode({ kind: 'idle' }); setDraft(draftOf()); setTouched(false); setSaveError(undefined); };

  const save = () => {
    setTouched(true);
    if (hasErrors(errors) || saving || !token) return;
    const payload = toPayload(draft);
    setSaving(true);
    setSaveError(undefined);
    const editing = mode.kind === 'edit' ? mode.category : undefined;
    Promise.resolve()
      .then(() => (editing
        ? CategoriesService.UpdateMedicalServiceCategory({ ...editing, ...payload }, token)
        : CategoriesService.AddMedicalServiceCategory(payload, token)))
      .then(() => { cancelForm(); load(); onChanged?.(); })
      .catch((error: ErrorType) => setSaveError(error))
      .finally(() => setSaving(false));
  };

  const remove = (category: MedicalServiceCategory) => {
    if (!token) return;
    setDeletingId(category.id);
    setDeleteError(undefined);
    Promise.resolve()
      .then(() => CategoriesService.DeleteMedicalServiceCategory(category.id, token))
      .then(() => { setConfirmDeleteId(undefined); load(); onChanged?.(); })
      .catch((error: ErrorType) => setDeleteError(error))
      .finally(() => setDeletingId(undefined));
  };

  const deleteMessage = deleteError
    ? (isInUseError(deleteError) ? t('medicalServiceCategories.errors.inUse') : (deleteError.ErrorMessage || t('medicalServiceCategories.errors.delete')))
    : undefined;
  const nameError = touched && errors.name ? errors.name : undefined;

  return (
    <GuideDrawer
      open={isOpen}
      size="large"
      title={t('medicalServiceCategories.title')}
      subtitle={t('medicalServiceCategories.subtitle')}
      onDismiss={onClose}
      preventDismiss={saving || deletingId !== undefined}
      closeLabel={t('medicalServiceCategories.close')}
      secondaryAction={{ label: t('medicalServiceCategories.close') }}
      className="msc-drawer"
      testId="medical-service-categories-drawer"
    >
      <div className="msc-toolbar">
        <SearchBox
          className="msc-search"
          value={search}
          onChange={(_event, data) => setSearch(data.value)}
          placeholder={t('medicalServiceCategories.search')}
          aria-label={t('medicalServiceCategories.search')}
        />
        <Badge appearance="filled" color="informative" shape="circular" size="large">
          {t('medicalServiceCategories.count', { count: categories.length })}
        </Badge>
        <Button appearance="primary" icon={<Add16Regular />} onClick={startNew} disabled={mode.kind === 'new'} data-testid="msc-new">
          {t('medicalServiceCategories.new')}
        </Button>
      </div>

      {mode.kind !== 'idle' && (
        <GuideDrawerSection title={mode.kind === 'edit' ? t('medicalServiceCategories.form.editTitle') : t('medicalServiceCategories.form.newTitle')} className="msc-form" testId="msc-form">
          <GuideField label={t('medicalServiceCategories.form.name')} required error={nameError} hint={nameError ? undefined : t('medicalServiceCategories.form.nameHint')} counter={{ value: draft.name.length, max: NAME_MAX }}>
            <Input
              ref={nameRef}
              value={draft.name}
              maxLength={NAME_MAX}
              onChange={(_event, data) => setDraft((current) => ({ ...current, name: data.value }))}
              onKeyDown={(event) => { if (event.key === 'Enter') save(); if (event.key === 'Escape') cancelForm(); }}
              placeholder={t('medicalServiceCategories.form.namePlaceholder')}
              data-testid="msc-name"
            />
          </GuideField>
          <GuideField label={t('medicalServiceCategories.form.description')} error={touched && errors.description ? errors.description : undefined} counter={{ value: draft.description.length, max: DESCRIPTION_MAX }}>
            <Textarea
              value={draft.description}
              maxLength={DESCRIPTION_MAX}
              rows={2}
              resize="vertical"
              onChange={(_event, data) => setDraft((current) => ({ ...current, description: data.value }))}
              placeholder={t('medicalServiceCategories.form.descriptionPlaceholder')}
            />
          </GuideField>
          {saveError && (
            <GuideMessageBar intent="error" layout="multiline" data-testid="msc-save-error">
              <MessageBarBody>
                <MessageBarTitle>{t('medicalServiceCategories.errors.save')}</MessageBarTitle> {saveError.ErrorMessage}
                {saveError.Errors && saveError.Errors.length > 0 && <ul>{saveError.Errors.map((item) => <li key={item}>{item}</li>)}</ul>}
              </MessageBarBody>
            </GuideMessageBar>
          )}
          <div className="msc-form__actions">
            <Button appearance="secondary" onClick={cancelForm} disabled={saving}>{t('medicalServiceCategories.form.cancel')}</Button>
            <Button appearance="primary" onClick={save} disabled={saving || (touched && hasErrors(errors))} icon={saving ? <Spinner size="extra-tiny" /> : undefined} data-testid="msc-save">
              {mode.kind === 'edit' ? t('medicalServiceCategories.form.saveEdit') : t('medicalServiceCategories.form.create')}
            </Button>
          </div>
        </GuideDrawerSection>
      )}

      {deleteMessage && (
        <GuideMessageBar intent="error" data-testid="msc-delete-error">
          <MessageBarBody>{deleteMessage}</MessageBarBody>
        </GuideMessageBar>
      )}

      {loading && categories.length === 0 && (
        <Spinner size="extra-tiny" labelPosition="after" label={t('medicalServiceCategories.loading')} />
      )}

      {!loading && loadError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="msc-load-error">
          <MessageBarBody>
            <MessageBarTitle>{t('medicalServiceCategories.errors.load')}</MessageBarTitle> {loadError.ErrorMessage || ''}
          </MessageBarBody>
          <MessageBarActions>
            <Button onClick={load}>{t('medicalServiceCategories.retry')}</Button>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {!loading && loaded && !loadError && categories.length === 0 && mode.kind === 'idle' && (
        <div className="msc-empty" data-testid="msc-empty">
          <Tag20Regular />
          <span className="msc-empty__title">{t('medicalServiceCategories.empty.title')}</span>
          <span className="msc-muted">{t('medicalServiceCategories.empty.text')}</span>
          <Button appearance="primary" icon={<Add16Regular />} onClick={startNew}>{t('medicalServiceCategories.new')}</Button>
        </div>
      )}

      {categories.length > 0 && visible.length === 0 && (
        <div className="msc-muted">{t('medicalServiceCategories.noMatch', { search })}</div>
      )}

      {visible.length > 0 && (
        <ul className="msc-list">
          {visible.map((category) => {
            const isEditing = editingId === category.id;
            const isDeletingThis = deletingId === category.id;
            const askingDelete = confirmDeleteId === category.id;
            return (
              <li key={category.id} className={`msc-row${isEditing ? ' msc-row--editing' : ''}`} data-testid={`msc-row-${category.id}`}>
                <span className="msc-row__icon" aria-hidden><Tag20Regular /></span>
                <div className="msc-row__body">
                  <span className="msc-row__name" title={category.name}>{category.name}</span>
                  {category.description && <span className="msc-row__desc" title={category.description}>{category.description}</span>}
                  {askingDelete && (
                    <div className="msc-confirm">
                      <span>{t('medicalServiceCategories.delete.confirm', { name: category.name })}</span>
                      <Button size="small" appearance="primary" className="msc-btn--danger" disabled={isDeletingThis} icon={isDeletingThis ? <Spinner size="extra-tiny" /> : undefined} onClick={() => remove(category)} data-testid={`msc-delete-confirm-${category.id}`}>
                        {t('medicalServiceCategories.delete.yes')}
                      </Button>
                      <Button size="small" disabled={isDeletingThis} onClick={() => setConfirmDeleteId(undefined)}>{t('medicalServiceCategories.form.cancel')}</Button>
                    </div>
                  )}
                </div>
                {!askingDelete && (
                  <span className="msc-row__actions">
                    <Button appearance="subtle" size="small" title={t('medicalServiceCategories.edit')} aria-label={`${t('medicalServiceCategories.edit')} ${category.name}`} icon={<Edit16Regular />} onClick={() => startEdit(category)} disabled={deletingId !== undefined} />
                    <Button appearance="subtle" size="small" className="msc-icon-btn--danger" title={t('medicalServiceCategories.delete.yes')} aria-label={`${t('medicalServiceCategories.delete.yes')} ${category.name}`} icon={<Delete16Regular />} onClick={() => { setDeleteError(undefined); setConfirmDeleteId(category.id); }} disabled={deletingId !== undefined} />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </GuideDrawer>
  );
};

export default MedicalServiceCategoriesDrawerComponent;
